<template>
  <div
    ref="modalEl"
    class="modal fade modal-server-error"
    tabindex="-1"
    role="dialog"
    aria-labelledby="modalServerErrorLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header modal-server-error__header">
          <h5
            class="modal-title d-flex align-items-center"
            id="modalServerErrorLabel"
          >
            <i class="material-icons-round modal-server-error__icon me-2"
              >error_outline</i
            >
            {{ title }}
          </h5>
          <button
            type="button"
            class="btn-close"
            aria-label="Close"
            @click="hide"
          ></button>
        </div>
        <div class="modal-body modal-server-error__body">
          <div v-html="displayMessage"></div>
        </div>
        <div class="modal-footer modal-server-error__footer">
          <button
            type="button"
            class="btn btn-primary mb-0"
            @click="hide"
          >
            {{ buttonText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Modal from "bootstrap/js/dist/modal";

// A modal shown while its view is being torn down - an API error that arrives
// as the router navigates away - never gets to clean up after itself, and
// Bootstrap's dispose() drops its backdrop but leaves the body locked either
// way. Both leave the next page greyed out and unscrollable.
function releaseBodyIfNoModalLeft() {
  if (document.querySelector(".modal.show")) return;

  document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("overflow");
  document.body.style.removeProperty("padding-right");
}

// Shared error modal used to surface API/server failures. Parents drive it
// with `v-model:show` (or `:show` + `@update:show`), and any close gesture
// (X button, OK button, ESC, backdrop click) propagates a `false` upward so
// the parent's state stays in sync.
export default {
  name: "ModalServerError",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Error",
    },
    message: {
      type: [String, Object, Error],
      default: "",
    },
    buttonText: {
      type: String,
      default: "OK",
    },
  },
  emits: ["update:show", "close"],
  computed: {
    // Normalize whatever was passed (string, Error, axios error object, plain
    // object) into a readable HTML snippet for the modal body.
    displayMessage() {
      const msg = this.message;
      if (msg == null || msg === "") return "";
      if (typeof msg === "string") return msg;
      if (msg instanceof Error) return msg.message || String(msg);
      if (typeof msg === "object") {
        // Axios-style error shape: { response: { data: { result: { message } } } }
        const apiMessage =
          msg?.response?.data?.result?.message ||
          msg?.data?.result?.message ||
          msg?.message;
        if (apiMessage) return apiMessage;
        try {
          return JSON.stringify(msg);
        } catch (e) {
          return String(msg);
        }
      }
      return String(msg);
    },
  },
  watch: {
    show(val) {
      if (val) this.openModal();
      else this.closeModal();
    },
  },
  mounted() {
    const el = this.$refs.modalEl;
    if (!el) return;
    el.addEventListener("hidden.bs.modal", this.onHidden);
    if (this.show) this.openModal();
  },
  unmounted() {
    const el = this.$refs.modalEl;
    if (!el) return;
    el.removeEventListener("hidden.bs.modal", this.onHidden);
    Modal.getInstance(el)?.dispose();
    releaseBodyIfNoModalLeft();
  },
  methods: {
    openModal() {
      const el = this.$refs.modalEl;
      if (!el) return;
      Modal.getOrCreateInstance(el).show();
      // Bootstrap gives every backdrop the same z-index, so over another modal
      // this one's backdrop lands under it and leaves it undimmed. show()
      // appends the backdrop synchronously, so lift it to just below us.
      const backdrops = document.querySelectorAll(".modal-backdrop");
      const backdrop = backdrops[backdrops.length - 1];
      if (backdrop) backdrop.style.zIndex = "1075";
    },
    closeModal() {
      const el = this.$refs.modalEl;
      if (el) Modal.getOrCreateInstance(el).hide();
    },
    hide() {
      this.closeModal();
    },
    onHidden() {
      this.$emit("update:show", false);
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
/* Stack above any other modal in the app — server errors should always be
   reachable, even when triggered from inside another open modal. */
.modal-server-error {
  z-index: 1080;
}

.modal-server-error__header {
  border-bottom: 1px solid #f1d6d6;
  background: #fdecea;
  color: #b3261e;
}

.modal-server-error__icon {
  color: #b3261e;
  font-size: 24px;
}

.modal-server-error__body {
  padding: 20px 24px;
  color: #45464d;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}

.modal-server-error__footer {
  border-top: 1px solid #e6e8ea;
}

.modal-server-error__footer .btn-primary {
  background: #b3261e;
  border-color: #b3261e;
}

.modal-server-error__footer .btn-primary:hover,
.modal-server-error__footer .btn-primary:focus {
  background: #8c1d17;
  border-color: #8c1d17;
}
</style>
